/**
 * Entitlement Service — single source of truth for feature/limit checks.
 *
 * Usage:
 *   const ent = await getEntitlements(accountId);
 *   ent.can('crm.full')                   // boolean or enum value
 *   ent.withinLimit('inventory.items', 1) // { allowed, remaining, cap }
 *
 * The server is authoritative. The client holds a read-only snapshot.
 * Never hard-code plan names in feature logic — always use feature keys.
 */

import { createClient } from '@/lib/supabase/client';

export interface EntitlementSnapshot {
  accountId: string;
  planSlug: string | null;
  status: string;
  version: number;
  features: Record<string, string>; // { "crm.full": "true", "pl.reporting": "advanced" }
  limits: Record<string, number>; // { "inventory.items": 5000, "seats": 3 } — -1 = unlimited
  trialEndAt: string | null;
  currentPeriodEnd: string | null;
  updatedAt: string;
}

export interface LimitCheck {
  allowed: boolean;
  remaining: number | null; // null when unlimited
  cap: number; // -1 when unlimited
  current: number;
}

export interface EntitlementClient {
  snapshot: EntitlementSnapshot;
  /** Returns the feature value ("true"/"false" or enum like "basic"/"advanced") */
  can(featureKey: string): string;
  /** Returns true if the boolean feature is enabled */
  isEnabled(featureKey: string): boolean;
  /** Checks if n more units of a resource can be consumed */
  withinLimit(resourceKey: string, n?: number): LimitCheck;
  /** Returns true if the subscription is in an active/trialing state */
  isActive(): boolean;
  /** Returns true if the account is in a restricted read-only state */
  isRestricted(): boolean;
}

const ACTIVE_STATUSES = new Set(['active', 'trialing']);
const RESTRICTED_STATUSES = new Set(['past_due', 'cancelled', 'expired']);

export function buildEntitlementClient(snapshot: EntitlementSnapshot): EntitlementClient {
  return {
    snapshot,

    can(featureKey: string): string {
      return snapshot.features[featureKey] ?? 'false';
    },

    isEnabled(featureKey: string): boolean {
      const val = snapshot.features[featureKey];
      return val === 'true' || (val !== undefined && val !== 'false' && val !== '');
    },

    withinLimit(resourceKey: string, n = 1): LimitCheck {
      const cap = snapshot.limits[resourceKey] ?? 0;
      if (cap === -1) {
        // Unlimited
        return { allowed: true, remaining: null, cap: -1, current: 0 };
      }
      // We'd need current usage from usage_counters — default to 0 here;
      // the full check happens server-side. Client uses this for UI display only.
      const current = 0;
      const remaining = Math.max(0, cap - current);
      return { allowed: remaining >= n, remaining, cap, current };
    },

    isActive(): boolean {
      return ACTIVE_STATUSES.has(snapshot.status);
    },

    isRestricted(): boolean {
      return RESTRICTED_STATUSES.has(snapshot.status);
    },
  };
}

/**
 * Fetch resolved entitlements for the current authenticated user.
 * Falls back to a minimal "no plan" snapshot on error.
 */
export async function getMyEntitlements(): Promise<EntitlementClient> {
  const supabase = createClient();

  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return buildEntitlementClient(noEntitlements(''));

    const { data, error } = await supabase
      .from('account_entitlements')
      .select('*')
      .eq('account_id', user.id)
      .maybeSingle();

    if (error || !data) {
      // No entitlement row yet — try to derive from subscription
      return await deriveFromSubscription(user.id);
    }

    const snapshot: EntitlementSnapshot = {
      accountId: data.account_id,
      planSlug: data.plan_slug,
      status: data.status,
      version: data.version,
      features: (data.features as Record<string, string>) ?? {},
      limits: (data.limits as Record<string, number>) ?? {},
      trialEndAt: data.trial_end_at,
      currentPeriodEnd: data.current_period_end,
      updatedAt: data.updated_at,
    };

    return buildEntitlementClient(snapshot);
  } catch {
    return buildEntitlementClient(noEntitlements(''));
  }
}

/**
 * Derive entitlements from the subscription + plan_features/plan_limits tables.
 * Used when account_entitlements row doesn't exist yet (e.g. new accounts).
 */
async function deriveFromSubscription(accountId: string): Promise<EntitlementClient> {
  const supabase = createClient();

  try {
    const { data: sub } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('account_id', accountId)
      .in('status', ['active', 'trialing', 'past_due'])
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!sub) return buildEntitlementClient(noEntitlements(accountId));

    const [featureResult, limitResult] = await Promise.all([
      supabase
        .from('plan_features')
        .select('feature_key, feature_value')
        .eq('plan_slug', sub.plan_slug),
      supabase
        .from('plan_limits')
        .select('resource_key, limit_value')
        .eq('plan_slug', sub.plan_slug),
    ]);
    const featureRows = featureResult.data;
    const limitRows = limitResult.data;

    const features: Record<string, string> = {};
    (featureRows ?? []).forEach((r: { feature_key: string; feature_value: string }) => {
      features[r.feature_key] = r.feature_value;
    });

    const limits: Record<string, number> = {};
    (limitRows ?? []).forEach((r: { resource_key: string; limit_value: number }) => {
      limits[r.resource_key] = r.limit_value;
    });

    const snapshot: EntitlementSnapshot = {
      accountId,
      planSlug: sub.plan_slug,
      status: sub.status,
      version: 1,
      features,
      limits,
      trialEndAt: sub.trial_end_at,
      currentPeriodEnd: sub.current_period_end,
      updatedAt: new Date().toISOString(),
    };

    return buildEntitlementClient(snapshot);
  } catch {
    return buildEntitlementClient(noEntitlements(accountId));
  }
}

function noEntitlements(accountId: string): EntitlementSnapshot {
  return {
    accountId,
    planSlug: null,
    status: 'inactive',
    version: 0,
    features: {},
    limits: {},
    trialEndAt: null,
    currentPeriodEnd: null,
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Provision entitlements for an account after subscription activation.
 * Idempotent — safe to call multiple times with the same subscription.
 */
export async function provisionEntitlements(
  accountId: string,
  planSlug: string,
  subscriptionId: string,
  status: string,
  trialEndAt: string | null,
  currentPeriodEnd: string | null
): Promise<void> {
  const supabase = createClient();

  const [featureResult2, limitResult2] = await Promise.all([
    supabase.from('plan_features').select('feature_key, feature_value').eq('plan_slug', planSlug),
    supabase.from('plan_limits').select('resource_key, limit_value').eq('plan_slug', planSlug),
  ]);
  const featureRows = featureResult2.data;
  const limitRows = limitResult2.data;

  const features: Record<string, string> = {};
  (featureRows ?? []).forEach((r: { feature_key: string; feature_value: string }) => {
    features[r.feature_key] = r.feature_value;
  });

  const limits: Record<string, number> = {};
  (limitRows ?? []).forEach((r: { resource_key: string; limit_value: number }) => {
    limits[r.resource_key] = r.limit_value;
  });

  // Upsert entitlements — bump version for cache busting
  await supabase.from('account_entitlements').upsert(
    {
      account_id: accountId,
      subscription_id: subscriptionId,
      plan_slug: planSlug,
      status,
      features,
      limits,
      trial_end_at: trialEndAt,
      current_period_end: currentPeriodEnd,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'account_id' }
  );
}
